// Detach (retain data) flow. Detaching deprovisions the DiskAttachment
// only — the underlying disk and its data are kept and the disk can be
// re-attached to a service later.
import { deprovisionDiskAttachment } from "@app/deploy";
import { useDispatch, useLoader, useLoaderSuccess } from "@app/react";
import { environmentActivityUrl } from "@app/routes";
import type { DeployDiskAttachment, DeployPersistentDisk } from "@app/types";
import { Banner, BannerMessages } from "@app/ui/shared/banner";
import { Button } from "@app/ui/shared/button";
import { IconAlertTriangle } from "@app/ui/shared/icons";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
} from "@app/ui/shared/modal";
import { ModalWrapper } from "@app/ui/shared/modal-portal";
import { useNavigate } from "react-router-dom";
import { DiskRestartWarning } from "./disk-restart-warning";

interface DetachPersistentDiskModalProps {
  isOpen: boolean;
  disk: DeployPersistentDisk;
  attachment: DeployDiskAttachment;
  // Handle of the service the attachment targets, for human-readable copy.
  serviceHandle: string;
  onClose: () => void;
}

export const DetachPersistentDiskModal = ({
  isOpen,
  disk,
  attachment,
  serviceHandle,
  onClose,
}: DetachPersistentDiskModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loader = useLoader(deprovisionDiskAttachment);

  useLoaderSuccess(loader, () => {
    // TODO: use appActivityUrl once operations properly populate targetResources
    // with the service and app being detached from.
    navigate(environmentActivityUrl(disk.environmentId));
  });

  const handleConfirm = () => {
    dispatch(deprovisionDiskAttachment({ id: attachment.id }));
  };

  if (!isOpen) return null;

  const serviceLabel = serviceHandle || "this service";

  return (
    <ModalWrapper>
      <Modal isOpen={isOpen} onClose={onClose} className="min-w-[600px]">
        <ModalHeading
          icon={<IconAlertTriangle variant="sm" className="text-yellow-500" />}
          title={`Detach Disk: ${disk.handle}`}
          onClose={onClose}
        />
        <ModalBody className="pb-0">
          <p className="text-gray-700">
            This will detach <strong>{disk.handle}</strong> from{" "}
            <strong>{serviceLabel}</strong>
            {attachment.mountPoint ? (
              <>
                {" "}
                (mounted at <strong>{attachment.mountPoint}</strong>)
              </>
            ) : null}
            .
          </p>
          <DiskRestartWarning className="mt-4" />
          <Banner variant="info" className="mt-4">
            Detaching a disk will not delete it. It can be re-attached to a
            service from the Environment's Disk Storage tab.
          </Banner>

          <BannerMessages className="mt-4" {...loader} />
        </ModalBody>
        <ModalFooter
          actions={[
            <Button key="cancel" variant="white" onClick={onClose}>
              Cancel
            </Button>,
            <Button
              key="confirm"
              variant="delete"
              onClick={handleConfirm}
              isLoading={loader.isLoading}
            >
              Detach Disk
            </Button>,
          ]}
        />
      </Modal>
    </ModalWrapper>
  );
};
